// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

/** Section heading with an id for the sidebar anchor / scroll-spy. */
export const H2: React.FC<{ id?: string; children: React.ReactNode }> = ({
  id,
  children
}) => (
  <h2
    id={id}
    className="font-display text-[20px] font-bold text-content-primary mt-[42px] mb-[14px] tracking-[-0.3px]"
  >
    {children}
  </h2>
);

/** Sub-heading within a section (for example a dial group under a tab). */
export const H3: React.FC<{ id?: string; children: React.ReactNode }> = ({
  id,
  children
}) => (
  <h3
    id={id}
    className="font-display text-[16px] font-bold text-content-primary mt-[26px] mb-[10px] tracking-[-0.2px]"
  >
    {children}
  </h3>
);

export const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[14px] leading-[1.75] text-content-secondary mb-[12px]">
    {children}
  </p>
);

/** A screenshot with an optional caption, styled to match the rest of the docs. */
export const DocImage: React.FC<{
  src: string;
  alt: string;
  caption?: string;
}> = ({ src, alt, caption }) => (
  <figure className="m-0 my-[16px]">
    <img
      src={src}
      alt={alt}
      className="max-w-full w-auto rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
    />
    {caption && (
      <figcaption className="mt-[8px] text-[12.5px] text-slate-500 leading-[1.55]">
        {caption}
      </figcaption>
    )}
  </figure>
);

/** Shared bullet styling. Numbered steps use <Steps> below. */

/**
 * Numbered steps as bubbles — the same marker the TouchPose docs use, so a
 * numbered step looks the same wherever it appears on the site.
 *
 * Takes its children as an ARRAY rather than <li> elements: the bubble and the
 * step body are two flex siblings, so the component has to own the <li> to
 * place them. Each child is one step, in order.
 */
export const Steps: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <ol className="list-none p-0 m-0 mb-[16px]">
    {React.Children.toArray(children).map((step, i) => (
      <li key={i} className="flex gap-[16px] mb-[20px] items-start">
        <span className="shrink-0 w-[28px] h-[28px] rounded-full bg-surface-hover border-[1.5px] border-border-brand flex items-center justify-center font-display text-[13px] font-bold text-blue-link">
          {i + 1}
        </span>
        <div className="mt-[4px] text-[14px] leading-[1.75] text-content-secondary">
          {step}
        </div>
      </li>
    ))}
  </ol>
);
export const UL_CLASS =
  'pl-[20px] m-0 mb-[12px] list-disc text-[14px] leading-[1.8] text-content-secondary marker:text-slate-400';

/**
 * Page hero: title, a short intro, and (where we have one) the how-to video
 * framed at the top of the page.
 */
export const DocHero: React.FC<{
  title: string;
  intro: React.ReactNode;
  videoSrc?: string;
  videoCaption?: string;
}> = ({ title, intro, videoSrc, videoCaption }) => {
  const { t } = useTranslation();

  return (
    <>
      <h1 className="font-display text-[28px] md:text-[32px] font-extrabold text-content-primary mb-[12px] tracking-[-0.5px]">
        {title}
      </h1>
      <p className="text-[15px] leading-[1.7] text-content-secondary mb-[24px] max-w-[680px]">
        {intro}
      </p>
      {videoSrc && (
        <figure className="m-0 mb-[8px]">
          <video
            controls
            playsInline
            preload="metadata"
            className="w-full rounded-[14px] border border-surface-hover bg-black shadow-[0_6px_24px_rgba(0,0,0,0.10)]"
          >
            <source src={videoSrc} type="video/mp4" />
            {t('eyeDocs.shared.videoUnsupported')}
          </video>
          {videoCaption && (
            <figcaption className="mt-[10px] text-[13px] text-slate-500 leading-[1.6]">
              {videoCaption}
            </figcaption>
          )}
        </figure>
      )}
    </>
  );
};

/** Sibling guide pages — labels reuse the sidebar's own keys. */
const GUIDE_PAGES = [
  { to: '/squarebit-eye/docs/parameters', key: 'eyeDocs.layout.parameters' },
  { to: '/squarebit-eye/docs/textures', key: 'eyeDocs.layout.textures' },
  { to: '/squarebit-eye/docs/rigging', key: 'eyeDocs.layout.rigging' }
];

/** Footer with links to the sibling guide pages + setup docs. */
export const DocFooter: React.FC<{ current: string }> = ({ current }) => {
  const { t } = useTranslation();

  return (
    <div className="border-t border-surface-hover mt-[44px] pt-[22px]">
      <p className="text-[12px] font-semibold tracking-[0.05em] uppercase text-slate-400 mb-[10px]">
        {t('eyeDocs.shared.otherGuides')}
      </p>
      <div className="flex flex-wrap gap-[8px] mb-[18px]">
        {GUIDE_PAGES.filter((p) => p.to !== current).map((p) => (
          <Link
            key={p.to}
            to={p.to}
            className="text-[13px] font-medium text-blue-link no-underline px-[12px] py-[6px] rounded-[8px] border border-surface-hover hover:border-blue transition-colors"
          >
            {t(p.key)} →
          </Link>
        ))}
      </div>
      <p className="text-[13px] text-slate-500 leading-[1.6]">
        <Trans
          i18nKey="eyeDocs.shared.needSetupHelp"
          components={{
            install: (
              <Link
                to="/squarebit-eye/docs/installation"
                className="text-blue-link no-underline font-medium"
              />
            ),
            contact: (
              <Link
                to="/contact"
                className="text-blue-link no-underline font-medium"
              />
            )
          }}
        />
      </p>
    </div>
  );
};
